import { plantuml } from '@mdit/plugin-plantuml'
import { useReactFlow, NodeProps } from '@xyflow/react'
import DOMPurify from 'dompurify'
import { ExternalLink } from 'lucide-react'
import MarkdownIt from 'markdown-it'
import React, { useState, useEffect, useRef, useMemo, memo } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'

// Import AI command utilities
import useDataSettings from '@/hooks/useDataSettings'
import {
  AICommand,
  AICommandType,
  AICommandStatus,
  createAICommand,
  isCommandComplete,
  isCommandError,
  createNodeCommandPayload,
  useAICommandState,
} from '@/utils/aiCommandUtils'
import NodeBaseActions from '../base/base-actions/NodeBaseActions'
import NodeBaseContainer from '../base/base-container/NodeBaseContainer'

/**
 * ✅ SYNCNODEDATA PATTERN COMPLIANT
 * 
 * This component follows the syncNodeData pattern to prevent data loss and ensure 
 * proper collaborative state management:
 * 
 * - Local state initialized with safe defaults, synced FROM collaborative state via useEffect
 * - Only calls syncNodeData on explicit user actions (blur, save, click, etc.)
 * - Never calls syncNodeData in useEffect that listens to collaborative state
 * - Uses separate handlers for local state updates and collaborative sync
 * - Prevents race conditions between local and collaborative state
 */

const plantUmlBaseUrl =
  (import.meta as any).env?.VITE_API_BASE_URL === 'http://localhost:4040'
    ? 'http://localhost:8080/plantuml'
    : 'https://studio-redux.spechops.com/plantuml'
console.log('baseUrl for syncedProjectStore is ' + plantUmlBaseUrl)

// PlantUML server configuration
const plantumlOptions = { server: plantUmlBaseUrl }

// Configure markdown-it with plantuml plugin
const mdIt = new MarkdownIt().use(plantuml, plantumlOptions)

interface NodeDiagramsData {
  content?: string
  renderedDiagram?: string
  statusMessage?: string
  label?: string
  isMinimized?: boolean
  isLocked?: boolean
  aiSuggestions?: string[]
  designPrompt?: string
  diagramType?: string
  showDesignMode?: boolean
  assignedEmails?: string[] | string
  documentRequest?: AICommand
  minimizeLevel?: 'normal' | 'medium' | 'minimum'
  minifiedInfo?: {
    type?: string
    content?: string
    prompt?: string
  }
  // Add dataSettings for type safety
  dataSettings?: any
}

const DEFAULT_DIAGRAM = `@startuml
actor User
participant "Web Application" as App
database "Database" as DB

User -> App: Request Data
App -> DB: Query Data
DB --> App: Return Results
App --> User: Display Results
@enduml`

const DIAGRAM_TYPES = [
  { value: 'sequence', label: 'Sequence Diagram' },
  { value: 'class', label: 'Class Diagram' },
  { value: 'activity', label: 'Activity Diagram' },
  { value: 'component', label: 'Component Diagram' },
  { value: 'state', label: 'State Diagram' },
  { value: 'object', label: 'Object Diagram' },
  { value: 'deployment', label: 'Deployment Diagram' },
  { value: 'usecase', label: 'Use Case Diagram' },
  { value: 'er', label: 'Entity Relationship Diagram' },
  { value: 'timing', label: 'Timing Diagram' },
  { value: 'network', label: 'Network Diagram' },
  { value: 'mindmap', label: 'Mind Map' },
  { value: 'gantt', label: 'Gantt Chart' },
]

const DIAGRAM_HELP = {
  sequence: 'Shows interactions between objects in sequential order',
  class: 'Shows classes, interfaces, and their relationships',
  activity: 'Shows workflows, processes or procedures',
  component: 'Shows components and dependencies between them',
  state: 'Shows states and transitions between them',
  object: 'Shows objects and their relationships at a specific point in time',
  deployment: 'Shows physical deployment of artifacts on nodes',
  usecase: 'Shows actors, their goals and system requirements',
  er: 'Shows entities, attributes and relationships for data modeling',
  timing: 'Shows changes in states over time',
  network: 'Shows network devices and their connections',
  mindmap: 'Shows hierarchical information and connections between ideas',
  gantt: 'Shows project schedule with tasks and milestones',
}

const EXAMPLE_PROMPTS = {
  sequence:
    'Create a sequence diagram showing a user logging in and retrieving their profile data',
  class:
    'Design a class diagram for an e-commerce system with products, customers, orders and payments',
  activity:
    'Create an activity diagram for the checkout process in an online store',
  component:
    'Design a component diagram for a microservices architecture with API gateway',
  state: 'Create a state diagram for a document approval workflow',
  object:
    'Design an object diagram showing the state of a shopping cart during checkout',
  deployment: 'Create a deployment diagram for a cloud-based web application',
  usecase: 'Design a use case diagram for a library management system',
  er: 'Create an entity relationship diagram for a hospital management system',
  timing: 'Design a timing diagram for a traffic light control system',
  network:
    'Create a network diagram for a small office with servers, workstations and network equipment',
  mindmap: 'Design a mind map for planning a software project',
  gantt: 'Create a Gantt chart for a software development lifecycle',
}

const NodeDiagrams: React.FC<NodeProps & { data: NodeDiagramsData }> = (
  props
) => {
  const { id, data, selected } = props
  const { updateNodeData } = useReactFlow()

  // Collaborative state: local copies, initialized empty, synced from data.<field>
  const [localContent, setLocalContent] = useState('')
  const [localRenderedDiagram, setLocalRenderedDiagram] = useState('')
  const [localStatusMessage, setLocalStatusMessage] = useState('')
  const [localAiSuggestions, setLocalAiSuggestions] = useState<string[]>([])
  const [localIsLocked, setLocalIsLocked] = useState(false)
  const [localIsMinimized, setLocalIsMinimized] = useState(false)
  const [localEditingTitleValue, setLocalEditingTitleValue] = useState('')
  const [localShowDesignMode, setLocalShowDesignMode] = useState(false)
  const [localDesignPrompt, setLocalDesignPrompt] = useState('')
  const [localDiagramType, setLocalDiagramType] = useState('sequence')
  const [localAssignedEmails, setLocalAssignedEmails] = useState<string[]>([])
  const [minimizeLevel, setMinimizeLevel] = useState<
    'normal' | 'medium' | 'minimum'
  >('normal')
  const [isMinimized, setIsMinimized] = useState(false)

  // Local UI-only state
  const [localIsEditingTitle, setLocalIsEditingTitle] = useState(false)
  const [localDiagramError, setLocalDiagramError] = useState<string | null>(
    null
  )
  const [localShowHelpDialog, setLocalShowHelpDialog] = useState(false)
  const [localRenderedSuggestions, setLocalRenderedSuggestions] = useState<
    string[]
  >([])
  const [localShowDeleteDialog, setLocalShowDeleteDialog] = useState(false)
  const [localItemToDelete, setLocalItemToDelete] = useState(null)
  const [localIsGenerating, setLocalIsGenerating] = useState(false)
  const [isDataSettingsDialogVisible, setIsDataSettingsDialogVisible] =
    useState(false)

  const titleInputRef = useRef<HTMLInputElement>(null)
  const diagramContainerRef = useRef<HTMLDivElement>(null)
  const promptTextareaRef = useRef<HTMLTextAreaElement>(null)
  const diagramTextareaRef = useRef<HTMLTextAreaElement>(null)

  // Sync local collaborative state from data.<field>
  useEffect(() => {
    setLocalContent(data.content ?? DEFAULT_DIAGRAM)
  }, [data.content])
  useEffect(() => {
    setLocalRenderedDiagram(data.renderedDiagram ?? '')
  }, [data.renderedDiagram])
  useEffect(() => {
    setLocalStatusMessage(data.statusMessage ?? '')
  }, [data.statusMessage])
  useEffect(() => {
    setLocalAiSuggestions(data.aiSuggestions ?? [])
  }, [data.aiSuggestions])
  useEffect(() => {
    setLocalIsLocked(data.isLocked ?? false)
  }, [data.isLocked])
  useEffect(() => {
    setLocalIsMinimized(data.isMinimized ?? false)
  }, [data.isMinimized])
  useEffect(() => {
    setLocalEditingTitleValue(data.label ?? '')
  }, [data.label])
  useEffect(() => {
    setLocalShowDesignMode(data.showDesignMode ?? false)
  }, [data.showDesignMode])
  useEffect(() => {
    setLocalDesignPrompt(data.designPrompt ?? '')
  }, [data.designPrompt])
  useEffect(() => {
    setLocalDiagramType(data.diagramType ?? 'sequence')
  }, [data.diagramType])
  useEffect(() => {
    setLocalAssignedEmails(
      Array.isArray(data.assignedEmails)
        ? data.assignedEmails
        : data.assignedEmails
          ? data.assignedEmails.trim().split(/\s+/).filter(Boolean)
          : []
    )
  }, [data.assignedEmails])

  // Initialize minimize level from data
  useEffect(() => {
    if (data.minimizeLevel === undefined) {
      updateNodeData(id, { ...data, minimizeLevel: 'normal' })
    } else {
      setMinimizeLevel(data.minimizeLevel)
      setIsMinimized(data.minimizeLevel !== 'normal')
    }
  }, [data.minimizeLevel, id, data, updateNodeData])

  // Keep minifiedInfo in sync with source data
  useEffect(() => {
    const minifiedInfo: Record<string, any> = {}

    if (data.diagramType) {
      minifiedInfo.type = data.diagramType
    }

    if (data.content) {
      minifiedInfo.content = 'Generated'
    }

    if (data.designPrompt && typeof data.designPrompt === 'string' && data.designPrompt.trim()) {
      minifiedInfo.prompt =
        data.designPrompt.substring(0, 50) + (data.designPrompt.length > 50 ? '...' : '')
    }

    // Only update if there are actual changes
    const currentMinified = data.minifiedInfo || {}
    const hasChanges =
      JSON.stringify(currentMinified) !== JSON.stringify(minifiedInfo)

    if (hasChanges) {
      updateNodeData(id, { ...data, minifiedInfo })
    }
  }, [
    data.diagramType,
    data.content,
    data.designPrompt,
    data.minifiedInfo,
    id,
    data,
    updateNodeData,
  ])
  // ✅ REMOVED: useEffect calling syncNodeData on collaborative state change
  // This was causing data loss on refresh - now diagram renders on explicit user actions only

  // Helper to sync node data (collaborative state)
  const syncNodeData = (newData: Partial<NodeDiagramsData>) => {
    updateNodeData(id, { ...data, ...newData })
  }

  // Function to render PlantUML diagram (collaborative update only)
  const renderDiagram = () => {
    try {
      setLocalDiagramError(null)
      const rendered = mdIt.render(localContent)
      syncNodeData({
        renderedDiagram: rendered,
        content: localContent,
        statusMessage: 'Diagram rendered successfully',
      })
    } catch (error) {
      setLocalDiagramError(
        `Error rendering diagram: ${error instanceof Error ? error.message : String(error)}`
      )
      syncNodeData({ statusMessage: 'Error rendering diagram' })
    }
  }

  // Render diagram on initial load if needed
  useEffect(() => {
    if (!data.renderedDiagram && data.content) {
      renderDiagram()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Check content for errors before rendering
  const validateDiagram = () => {
    if (!localContent.includes('@startuml')) {
      setLocalDiagramError('Missing @startuml directive')
      return false
    }
    if (!localContent.includes('@enduml')) {
      setLocalDiagramError('Missing @enduml directive')
      return false
    }
    setLocalDiagramError(null)
    return true
  }

  // Handle lock/unlock
  const handleLock = () => {
    syncNodeData({ isLocked: !localIsLocked })
  }

  // Handle minimize/maximize
  const handleMinimize = () => {
    syncNodeData({ isMinimized: !localIsMinimized })
  }

  // Handle delete
  const confirmDelete = (item) => {
    setLocalItemToDelete(item)
    setLocalShowDeleteDialog(true)
  }

  const handleDeleteConfirmed = () => {
    if (localItemToDelete) {
      if ((window as any).__flowDeleteNode) {
        ;(window as any).__flowDeleteNode(id)
      }
    }
    setLocalShowDeleteDialog(false)
    setLocalItemToDelete(null)
  }

  // Handle duplicate
  const handleDuplicate = () => {
    if ((window as any).__flowDuplicateNode) {
      ;(window as any).__flowDuplicateNode(id)
    }
  }

  // Function to handle title edit save
  const handleTitleSave = () => {
    syncNodeData({ label: localEditingTitleValue })
    setLocalIsEditingTitle(false)
  }

  // Function to handle keypresses in title input
  const handleTitleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSave()
    } else if (e.key === 'Escape') {
      setLocalIsEditingTitle(false)
      setLocalEditingTitleValue(data.label || '')
    }
  }

  // Focus input when editing starts
  useEffect(() => {
    if (localIsEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus()
      titleInputRef.current.select()
    }
  }, [localIsEditingTitle])

  // Toggle between design mode and code mode
  const toggleDesignMode = () => {
    syncNodeData({ showDesignMode: !localShowDesignMode })
  }

  // Use AI command state for diagram generation
  const diagramGenerateState = useAICommandState(
    data.documentRequest,
    AICommandType.DIAGRAMS_GENERATE
  )
  const diagramDesignState = useAICommandState(
    data.documentRequest,
    AICommandType.DIAGRAMS_DESIGN
  )

  // Update isGenerating based on command states
  useEffect(() => {
    setLocalIsGenerating(
      diagramGenerateState.isProcessing || diagramDesignState.isProcessing
    )
  }, [diagramGenerateState.isProcessing, diagramDesignState.isProcessing])

  // Handle generating AI suggestions for diagram improvement
  const handleGetAISuggestions = () => {
    if (!localContent.trim()) {
      syncNodeData({
        statusMessage: 'Error: Please enter a diagram to analyze.',
      })
      return
    }
    const processingMsg = 'Generating AI suggestions...'
    syncNodeData({ statusMessage: processingMsg })
    const documentRequest = createAICommand(
      AICommandType.DIAGRAMS_GENERATE,
      AICommandStatus.STARTED,
      createNodeCommandPayload(id, {
        content: localContent,
        document: 'plantuml-diagram-suggestions',
      })
    )
    syncNodeData({ documentRequest })
  }

  // Generate diagram from natural language prompt
  const handleGenerateDiagramFromPrompt = () => {
    if (!localDesignPrompt.trim()) {
      syncNodeData({
        statusMessage: 'Error: Please enter a design description.',
      })
      return
    }
    const processingMsg = 'Generating diagram from description...'
    syncNodeData({
      statusMessage: processingMsg,
      documentRequest: createAICommand(
        AICommandType.DIAGRAMS_DESIGN,
        AICommandStatus.STARTED,
        createNodeCommandPayload(id, {
          prompt: localDesignPrompt,
          diagramType: localDiagramType,
          document: 'plantuml-diagram-generation',
        })
      ),
    })
  }

  // Use example prompt for selected diagram type
  const useExamplePrompt = () => {
    const example = EXAMPLE_PROMPTS[localDiagramType] || ''
    setLocalDesignPrompt(example)
    if (promptTextareaRef.current) {
      promptTextareaRef.current.focus()
    }
  }

  // Function to clear AI suggestions
  const clearAiSuggestions = () => {
    syncNodeData({ aiSuggestions: [], statusMessage: 'AI suggestions cleared' })
    setLocalRenderedSuggestions([])
  }

  // Add effect to update AI suggestions when system agent has finished processing
  useEffect(() => {
    if (!data.documentRequest) return

    // Handle diagram suggestions generation completion
    if (
      isCommandComplete(data.documentRequest, AICommandType.DIAGRAMS_GENERATE)
    ) {
      if (data.documentRequest.suggestions) {
        const suggestions = data.documentRequest.suggestions
        syncNodeData({
          aiSuggestions: suggestions,
          statusMessage: 'AI suggestions generated.',
          documentRequest: undefined,
        })
        setLocalRenderedSuggestions([])
        suggestions.forEach((suggestion, index) => {
          try {
            const rendered = mdIt.render(suggestion)
            setLocalRenderedSuggestions((prev) => {
              const newRendered = [...prev]
              newRendered[index] = rendered
              return newRendered
            })
          } catch (error) {
            // ignore rendering error for suggestion preview
          }
        })
      } else {
        syncNodeData({
          statusMessage: 'AI suggestions generated.',
          documentRequest: undefined,
        })
      }
    } else if (
      isCommandError(data.documentRequest, AICommandType.DIAGRAMS_GENERATE)
    ) {
      syncNodeData({
        statusMessage: 'Error generating AI suggestions.',
        documentRequest: undefined,
      })
    }

    // Handle design generation completion
    if (
      isCommandComplete(data.documentRequest, AICommandType.DIAGRAMS_DESIGN)
    ) {
      if (data.documentRequest.generatedDiagram) {
        const generatedContent = data.documentRequest.generatedDiagram
        try {
          const rendered = mdIt.render(generatedContent)
          syncNodeData({
            content: generatedContent,
            renderedDiagram: rendered,
            statusMessage: 'Diagram generated successfully!',
            showDesignMode: false,
            documentRequest: undefined,
          })
          setTimeout(() => {
            if (diagramTextareaRef.current) {
              diagramTextareaRef.current.focus()
            }
          }, 100)
        } catch (error) {
          syncNodeData({
            statusMessage: 'Error rendering generated diagram',
            documentRequest: undefined,
          })
        }
      } else {
        syncNodeData({
          statusMessage:
            'Error generating diagram. Please try a different description.',
          documentRequest: undefined,
        })
      }
    } else if (
      isCommandError(data.documentRequest, AICommandType.DIAGRAMS_DESIGN)
    ) {
      syncNodeData({
        statusMessage:
          'Error generating diagram. Please try a different description.',
        documentRequest: undefined,
      })
    }
  }, [data.documentRequest])

  // Handle refresh button click
  const handleRefreshDiagram = () => {
    if (validateDiagram()) {
      renderDiagram()
    } else {
      syncNodeData({ statusMessage: `Error: ${localDiagramError}` })
    }
  }

  // Apply an AI suggestion to the current diagram
  const applySuggestion = (suggestion: string) => {
    console.log(suggestion)
    syncNodeData({ content: suggestion })
    // No setLocalContent here; will update via useEffect on data.content
  }

  // Handle diagram type change
  const handleDiagramTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    syncNodeData({ diagramType: e.target.value })
  }

  // Add a handler for title change to sync with node data
  const handleTitleChange = (newTitle: string) => {
    syncNodeData({ label: newTitle })
  }
  const handleMinimizeLevelChange = (
    level: 'normal' | 'medium' | 'minimum'
  ) => {
    // ✅ FIXED: Only update collaborative state, local state will sync via useEffect
    updateNodeData(id, {
      ...data,
      minimizeLevel: level,
      isMinimized: level !== 'normal',
    })
  }

  // --- Data Settings logic (outgoing only) ---
  // Only expose outgoing data (no inputLinks)
  const outgoingFields = {
    content: {
      value: localContent,
      enabled: true,
      label: 'Diagram Code',
      description: 'PlantUML diagram code',
      permissions: { readable: true, writable: false },
    },
    renderedDiagram: {
      value: localRenderedDiagram,
      enabled: !!localRenderedDiagram,
      label: 'Rendered Diagram',
      description: 'Rendered diagram HTML',
      permissions: { readable: true, writable: false },
    },
    diagramType: {
      value: localDiagramType,
      enabled: true,
      label: 'Diagram Type',
      description: 'Type of diagram (sequence, class, etc.)',
      permissions: { readable: true, writable: false },
    },
    designPrompt: {
      value: localDesignPrompt,
      enabled: !!localDesignPrompt,
      label: 'Design Prompt',
      description: 'Natural language prompt for diagram generation',
      permissions: { readable: true, writable: false },
    },
    aiSuggestions: {
      value: JSON.stringify(localAiSuggestions),
      enabled: localAiSuggestions.length > 0,
      label: 'AI Suggestions',
      description: 'AI-generated diagram suggestions',
      permissions: { readable: true, writable: false },
    },
  }

  // Use useDataSettings for outgoing/exposed data only
  const {
    dataSettings,
    setDataSettings,
    handleDataSettingsChange,
    handleOpenDataSettingsDialog,
    prepareExposedDataFields,
  } = useDataSettings(
    id,
    data,
    (newData) => syncNodeData(newData),
    false, // showDebugInfo
    '', // context not relevant here
    new Map(), // sections not relevant
    '', // contentMarkdown not relevant
    setIsDataSettingsDialogVisible
  )  // ✅ FIXED: Only update local dataSettings, no syncNodeData in useEffect listening to collaborative state
  // This prevents data loss on refresh by not calling syncNodeData when collaborative state changes
  useEffect(() => {
    // Only outgoing/exposedData, no inputLinks
    const newDataSettings = {
      ...dataSettings,
      inputLinks: [],
      exposedData: outgoingFields,
      availableNodes: [],
      outputLinks: dataSettings?.outputLinks || [],
      autoSync: false,
    }
    if (
      JSON.stringify(dataSettings?.exposedData) !==
      JSON.stringify(outgoingFields)
    ) {
      setDataSettings(newDataSettings)
      // ❌ REMOVED: syncNodeData call from useEffect listening to collaborative state
      // syncNodeData({ dataSettings: newDataSettings })
    }
  }, [
    localContent,
    localRenderedDiagram,
    localDiagramType,
    localDesignPrompt,
    localAiSuggestions,
    dataSettings,
    outgoingFields,
    setDataSettings,
  ])

  // ✅ ADDED: Sync dataSettings on explicit user action
  const handleSyncDataSettings = () => {
    if (dataSettings) {
      syncNodeData({ dataSettings })
    }
  }

  // Compute status for NodeBaseContainer based on statusMessage
  const nodeStatus = useMemo(() => {
    if (!data?.statusMessage) return 'initial'
    const lowerMessage = data.statusMessage.toLowerCase()
    if (lowerMessage.includes('loading')) return 'loading'
    if (lowerMessage.includes('error')) return 'error'
    if (
      lowerMessage.includes('complete') ||
      lowerMessage.includes('successful')
    )
      return 'success'
    return 'initial'
  }, [data?.statusMessage])

  return (
    <NodeBaseContainer
      id={id}
      data={data}
      resizable={!localIsLocked}
      selected={selected} // ✅ CRITICAL: Make sure selected prop is passed!
      isProcessing={localIsGenerating}
      assignedEmails={localAssignedEmails}
      onAssignUsersClick={() => {
        const emailsStr = window.prompt(
          'Enter emails separated by space',
          localAssignedEmails.join(' ')
        )
        if (emailsStr !== null) {
          const emailsArr = emailsStr.trim().split(/\s+/).filter(Boolean)
          syncNodeData({ assignedEmails: emailsArr })
        }
      }}
      nodeTitle={data.label || 'Diagram generator'}
      onTitleChange={handleTitleChange}
      status={nodeStatus}
      minimizeLevel={data.minimizeLevel! || 'normal'}
      onMinimizeLevelChange={handleMinimizeLevelChange}
    >
      {(data.minimizeLevel || 'normal') === 'normal' && (
        <div className="flex flex-col h-full">
          <div className="flex justify-between items-center px-2 py-3">
            <NodeBaseActions
              nodeId={id}
              onDelete={() => confirmDelete(null)}
              onDuplicate={handleDuplicate}
              onLock={handleLock}
              onUnlock={localIsLocked}
              onMinimize={handleMinimize}
              onMaximize={localIsMinimized}
            />
            <div className="flex items-center space-x-2">
              <button
                className="px-2 py-1 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                onClick={() => setLocalShowHelpDialog(true)}
              >
                Help
              </button>
            </div>
          </div>

          {!localIsMinimized && (
            <div className="flex flex-col flex-grow px-2 pb-2 space-y-2">
              {!localIsLocked && (
                <div className="flex space-x-2 border-b pb-2">
                  <button
                    className={`px-4 py-1 rounded-t transition-colors ${
                      !localShowDesignMode
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                    }`}
                    onClick={() => localShowDesignMode && toggleDesignMode()}
                  >
                    Code Mode
                  </button>
                  <button
                    className={`px-4 py-1 rounded-t transition-colors ${
                      localShowDesignMode
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                    }`}
                    onClick={() => !localShowDesignMode && toggleDesignMode()}
                  >
                    Design Mode
                  </button>
                </div>
              )}

              {localShowDesignMode ? (
                // Design Mode UI
                <div className="flex-grow flex flex-col">
                  <div className="mb-2">
                    <label className="block text-sm font-medium mb-1">
                      Diagram Type:
                    </label>
                    <div className="flex mb-2">
                      <select
                        className="flex-1 p-2 border rounded"
                        value={localDiagramType}
                        onChange={handleDiagramTypeChange}
                        disabled={localIsLocked || localIsGenerating}
                      >
                        {DIAGRAM_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                      <button
                        className="ml-2 px-3 py-1 bg-gray-200 text-gray-800 text-sm rounded hover:bg-gray-300 transition-colors"
                        onClick={useExamplePrompt}
                        disabled={localIsLocked || localIsGenerating}
                        title="Use an example prompt for this diagram type"
                      >
                        Example
                      </button>
                    </div>
                    <p className="text-xs text-gray-600 mb-2">
                      {DIAGRAM_HELP[localDiagramType] ||
                        'Select a diagram type to see description'}
                    </p>
                  </div>
                  <div className="flex-grow flex flex-col">
                    <label className="block text-sm font-medium mb-1">
                      Describe what you want to diagram (be specific):
                    </label>
                    <textarea
                      ref={promptTextareaRef}
                      className="flex-grow p-2 border rounded nodrag resize-none font-sans text-sm mb-2"
                      placeholder={`Example: ${EXAMPLE_PROMPTS[localDiagramType] || 'Describe your diagram here...'}`}
                      value={localDesignPrompt}
                      onChange={(e) => {
                        setLocalDesignPrompt(e.target.value)
                      }}
                      onBlur={(e) =>
                        syncNodeData({ designPrompt: e.target.value })
                      }
                      disabled={localIsLocked || localIsGenerating}
                    />
                  </div>
                  <button
                    className="w-full py-2 px-4 bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:bg-gray-400"
                    onClick={handleGenerateDiagramFromPrompt}
                    disabled={
                      localIsLocked ||
                      localIsGenerating ||
                      !localDesignPrompt.trim()
                    }
                  >
                    {localIsGenerating
                      ? 'Generating Diagram...'
                      : 'Generate Diagram'}
                  </button>
                </div>
              ) : (
                // Code Mode UI
                <div className="flex-grow flex flex-col">
                  <div className="flex-grow relative min-h-0 w-full">
                    <div className="flex h-full space-x-2">
                      {/* PlantUML editor */}
                      <div className="flex-1 h-full">
                        <textarea
                          ref={diagramTextareaRef}
                          className="w-full h-full p-2 border rounded nodrag nowheel resize-none font-mono text-sm"
                          placeholder="Enter your PlantUML diagram code here..."
                          value={localContent}
                          onChange={(e) => {
                            setLocalContent(e.target.value)
                          }}
                          onBlur={() => syncNodeData({ content: localContent })}
                          disabled={localIsLocked}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => {
                            e.stopPropagation()
                          }}
                        />
                      </div>

                      {/* Rendered diagram display */}
                      <div
                        ref={diagramContainerRef}
                        className="flex-1 h-full border rounded p-2 overflow-auto bg-gray-50"
                      >
                        {localDiagramError ? (
                          <div className="text-red-500 p-2">
                            {localDiagramError}
                          </div>
                        ) : (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(localRenderedDiagram),
                            }}
                            className="flex flex-col items-center justify-center h-full gap-10"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {!localIsLocked && (
                    <div className="flex space-x-2 mt-2">
                      <button
                        className="flex-1 py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        onClick={handleRefreshDiagram}
                      >
                        Refresh Diagram
                      </button>
                      <button
                        className="flex-1 py-2 px-4 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                        onClick={handleGetAISuggestions}
                        disabled={localIsGenerating}
                      >
                        {localIsGenerating
                          ? 'Getting Suggestions...'
                          : 'Get AI Suggestions'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {localStatusMessage && (
                <div className="text-sm text-gray-600">
                  {localStatusMessage}
                </div>
              )}

              {localAiSuggestions &&
                localAiSuggestions.length > 0 &&
                !localShowDesignMode && (
                  <div className="overflow-auto w-full">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-semibold">AI Suggestions:</h3>
                      <button
                        className="py-1 px-3 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors"
                        onClick={clearAiSuggestions}
                      >
                        Clear Suggestions
                      </button>
                    </div>
                    <div className="space-y-4">
                      {localAiSuggestions.map((suggestion, index) => (
                        <div
                          key={`${id}-suggestion-${index}`}
                          className="border rounded p-3 bg-gray-50"
                        >
                          {/* Suggestion header */}
                          <div className="mb-2">
                            <span className="text-xs font-medium">
                              Suggestion {index + 1}
                            </span>
                          </div>

                          {/* Code and preview side by side */}
                          <div className="flex space-x-2">
                            {/* Code */}
                            <div className="w-1/2">
                              <pre className="whitespace-pre-wrap text-xs font-mono h-56 overflow-auto p-2 bg-white border rounded">
                                {suggestion}
                              </pre>
                            </div>

                            {/* Preview - always shown */}
                            <div className="w-1/2 border rounded p-2 bg-white">
                              <div className="text-xs font-medium mb-1">
                                Preview:
                              </div>
                              <div className="p-2 bg-gray-50 rounded h-48 overflow-auto flex items-center justify-center">
                                {localRenderedSuggestions[index] ? (
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: DOMPurify.sanitize(
                                        localRenderedSuggestions[index]
                                      ),
                                    }}
                                    className="w-full h-full flex items-center justify-center scale-[0.9] transform-origin-center"
                                    style={{ transformOrigin: 'center' }}
                                  />
                                ) : (
                                  <div className="text-xs text-gray-500">
                                    Rendering preview...
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="mt-2">
                            <button
                              className="py-1 px-3 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                              onClick={() => applySuggestion(suggestion)}
                            >
                              Apply This Suggestion
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}
          <AlertDialog
            open={localShowDeleteDialog}
            onOpenChange={setLocalShowDeleteDialog}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this diagram? This action
                  cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteConfirmed}>
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Dialog
            open={localShowHelpDialog}
            onOpenChange={(open) => setLocalShowHelpDialog(open)}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>PlantUML & Themes Help</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col space-y-4">
                <p>
                  There are two modes: Code mode, where you can craft diagrams
                  and get AI suggestions for different versions, and Design
                  mode, where you can create new diagrams from scratch using AI
                  assistance.
                </p>
                <p>
                  To set a theme, include "!theme amiga" at the top of your
                  PlantUML code after @startuml line.
                </p>
                <p>
                  Other available themes: simple, dark, gradient, handwritten.
                </p>
                <p>
                  PlantUML allows you to create diagrams from plain text. For
                  detailed documentation and examples, visit the PlantUML
                  website or refer to the manual (links provided below).
                </p>
              </div>
              <DialogFooter className="flex justify-end space-x-2">
                <Button variant="outline" asChild>
                  <a
                    href="https://plantuml.com"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center"
                  >
                    PlantUML-homepage <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                </Button>
                <Button variant="outline" asChild>
                  <a
                    href="https://pdf.plantuml.net/PlantUML_Language_Reference_Guide_en.pdf"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center"
                  >
                    Manual <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                </Button>
                <Button onClick={() => setLocalShowHelpDialog(false)}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </NodeBaseContainer>
  )
}

export default memo(NodeDiagrams)
